<template>
  <div class="app-content-wrapper">
    <div v-loading="isLoading">
      <div class="search-wrapper">
        <search-component
          ref="search"
          :search-condition="searchCondition"
          :search-buttons="searchButtonList"
          :fuzzySearchKey="'bankAccount'"
          :fuzzySearchPlaceholder="$t('message.contact.accountName')"
          :showFilterNumber="false"
          @toSearch="onSearch"
        >
        </search-component>
      </div>
      <div v-if="listInfo.content.length > 0" class="list-wrapper">
        <el-collapse v-model="activeList" accordion @change="handleChangeCollapse">
          <el-collapse-item v-for="(item) in listInfo.content" :key="item.cardInfoId" :name="item.cardInfoId">
            <template slot="title">
              <div class="contact-item-box">
                <div class="icon-contact-box">
                  <span>{{getBankAccount(item)}}</span>
                  <img class="icon-currency" :src="getCurrencyImage(item.payoutCurrency) ? require(`@/assets/images/currency/${item.payoutCurrency}.png`) : ''" />
                </div>
              </div>
              <div class="contact-item-info">
                <p class="contact-item-top">
                  <span class="contact-name">{{getBankAccountText(item)}}</span>
                  <el-tag :type="getAuditStatusTagType(item.payeeStatus)">
                    <el-badge :is-dot="item.payeeStatus === 'pending_update'" style="line-height: 1">
                      <span>{{getAuditStatusText(item.payeeStatus)}}</span>
                    </el-badge>
                  </el-tag>
                  <span v-if="item.defaultCard" class="default-contact">{{$t('message.contact.defaultAccount')}}</span>
                </p>
                <p class="contact-item-bottom">
                  <span class="contact-country">{{getCountryText(item.bankCountry)}}</span>
                  <span class="contact-bankno">{{item.cardNumber}}</span>
                </p>
              </div>
            </template>
            <details-component
              :row-column="2"
              :direction="'vertical'"
              :details-condition="item.condition">
            </details-component>
            <div class="item-button-box">
              <!-- 仅”审核通过“的收款方详情展示“去付款”操作 -->
              <!-- 商户业务类型为 B2B/B2C 时显示按钮 -->
              <el-button
                v-if="app.userLogin && (`${app.userLogin.businessType}` === '0' ||`${app.userLogin.businessType}` === '2') && (item.payeeStatus === 'pass' || item.payeeStatus === 'unnecessary')"
                v-permission="'transfer:contact:transfer'"
                class="item-button" @click="handleTransfer(item.cardInfoId)">{{$t('message.contact.transferMoney')}}</el-button>
              <!-- ”审核通过/无需审核/待更新“的收款方支持修改 -->
              <!-- 商户业务类型为 B2B/B2C 时显示按钮 -->
              <el-button
                v-if="app.userLogin && (`${app.userLogin.businessType}` === '0' ||`${app.userLogin.businessType}` === '2') &&
                (item.payeeStatus === 'pass' || item.payeeStatus === 'unnecessary' || item.payeeStatus === 'pending_update')"
                v-permission="'transfer:contact:edit'"
                class="item-button" @click="handleContactDetail(item.cardInfoId)">{{$t('message.contact.editContact')}}</el-button>
              <!-- ”审核通过“, “自有”但不是“默认提现账户”的，支持手动“设置默认提现账户”； -->
              <el-button
                v-if="(item.payeeStatus === 'pass' || item.payeeStatus === 'unnecessary') && (+item.accountBelong === 0) && !item.defaultCard"
                v-permission="'transfer:contact:setDefault'"
                class="item-button" @click="handleSettingDefault(item.cardInfoId)">{{$t('message.contact.setDefault')}}</el-button>
            </div>
          </el-collapse-item>
        </el-collapse>
        <div class="rhcn-pagination">
          <el-pagination v-if="listInfo.totalElements && listInfo.totalElements!=0"
            background
            :page-sizes="[10, 20, 50]"
            :current-page="Number(listInfo.current)"
            :page-size="listInfo.size"
            :total="listInfo.totalElements"
            layout="total, sizes, prev, pager,jumper,next"
            :prev-text="$t('message.tableTitleCommon.previous')"
            :next-text="$t('message.tableTitleCommon.next')"
            @current-change="onCurrentPageChange"
            @size-change="onPageSizeChange"/>
        </div>
      </div>
      <div v-if="!isLoading && listInfo.content.length === 0" class="contact-empty-box">
        <empty-component :empty-subtitle="$t('message.tableCommon.searcnEmpty')"></empty-component>
      </div>
    </div>
    <custom-message ref="customMessage"></custom-message>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';

import DetailsComponent from '@/components/base-details/details.vue';
import CustomMessage from '@/components/modal/message-box/custom-message.vue';
import { ShowSuccessMessage } from '@/components/modal/tips/message-tips.js';
import EmptyComponent from '@/components/table/empty-component.vue';
import SearchComponent from '@/components/table/search-component.vue';
import GlobalData from '@/utils/global/index';
import { getEnantiomericValue, initSelectedData } from '@/utils/tools/constant';
import { getCurrencyImage } from '@/utils/tools/currency';
import { getFileNameFromUrl } from '@/utils/tools/utils';
import {
  getContactList, getPayeeInfo,
  postDefaultCard, getCountryGroupListByName,
  downloadPayeeList,
} from '@/views/transfer/api/contact';
import {
  ACCOUNT_BELONG_OPTIONS, ENTITY_TYPE_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
} from '@/views/transfer/api/options';

export default {
  components: {
    SearchComponent,
    DetailsComponent,
    CustomMessage,
    EmptyComponent,
  },
  data() {
    return {
      isLoading: true,
      activeList: [],
      listInfo: {
        totalElements: 0,
        size: 10,
        current: 1,
        content: [],
      },
      searchCondition: [
        {
          key: 'payoutCurrency',
          name: 'message.tableCommon.currency',
          type: 'select',
          defValue: '',
          options: [],
        },
        {
          key: 'bankCountry',
          name: 'message.contact.bankCountryRegion',
          type: 'select',
          defValue: '',
          options: [],
        },
        {
          key: 'cardNumber',
          name: 'message.contact.accountNumber',
          type: 'input',
          defValue: '',
        },
        {
          key: 'id',
          name: 'message.contact.contactId',
          type: 'input',
          defValue: '',
        },
      ],
      searchButtonList: [{
        type: 'other',
        name: 'message.common.download',
        permissionCode: 'transfer:contact:download',
        func: async (params) => {
          if (!this.downloading) {
            this.downloading = true;
            try {
              await downloadPayeeList(params);
              ShowSuccessMessage(this.$t('message.common.downloadTask'));
            } catch (err) {
              //
            }
            this.downloading = false;
          }
        },
      }],
      countryList: [],
      contactInfoMap: {},
      materialNameList: [],
      auditStatusList: [],
    };
  },
  computed: {
    ...mapGetters([
      'app',
    ]),
  },
  created() {
    const userLogin = this.app.userLogin;
    // 类型为 B2B/B2C 时，显示
    if (userLogin && (userLogin.businessType === '0' || userLogin.businessType === '2')) {
      this.searchButtonList.push({
        type: 'other',
        name: 'message.contact.addContact',
        permissionCode: 'transfer:contact:add',
        func: async () => {
          this.$router.push({ path: '/transfer/contact/edit' });
        },
      });
    }
  },
  mounted() {
    this.initData();
  },
  methods: {
    getCurrencyImage,
    async initData() {
      try {
        const dict = await GlobalData.getDictList(['PAYOUT_CURRENCY', 'MATERIAL_NAME', 'PAYEE_STATUS', 'COUNTRY']);
        this.materialNameList = initSelectedData(dict.MATERIAL_NAME);
        this.auditStatusList = initSelectedData(dict.PAYEE_STATUS);
        this.countryList = initSelectedData(dict.COUNTRY);
        // 获取收款方国家、收款行国家
        const res = await getCountryGroupListByName({ countryGroupName: 'payment-ALL' });
        const bankCountryList = (res.countryGroupInfos || []).map((item) => ({
          label: item.description,
          value: item.value,
        }));

        if (this.$refs.search) {
          this.$refs.search.setSearchSelectOptions('payoutCurrency', initSelectedData(dict.PAYOUT_CURRENCY, 'value'));
          this.$refs.search.setSearchSelectOptions('bankCountry', initSelectedData(bankCountryList, 'label'));
        }
      } catch (err) {
        //
      }
      this.initQuery();
      this.onSearch();
    },
    onSearch(params = {}) {
      const searchParams = {
        current: 1,
        size: this.listInfo.size,
        ...params,
      };
      this.$router.push({
        query: searchParams,
      });
      this.fetchContactList(searchParams);
    },
    fetchContactList(params = {}) {
      this.isLoading = true;
      this.activeList = [];
      getContactList({
        current: this.listInfo.current,
        size: this.listInfo.size,
        ...params,
      }).then(res => {
        const isPayout = (this.app.userLogin && this.app.userLogin.businessType === '0');
        res.content.map((item) => {
          // 将无需审核的转换成审核通过
          item.payeeStatus = item.payeeStatus === 'unnecessary' ? 'pass' : item.payeeStatus;
          const listTemplate = [
            {
              titleBar: { title: 'message.contact.beneficiaryCategory' },
              key: 'accountBelong',
              textFunc: (data) => getEnantiomericValue(ACCOUNT_BELONG_OPTIONS, data.accountBelong),
              isDisplayFunc: (data) => data.accountBelong,
              isInternationalization: true,
            },
            {
              titleBar: { title: 'message.contact.beneficiaryType' },
              key: 'entityType',
              textFunc: (data) => getEnantiomericValue(ENTITY_TYPE_OPTIONS, data.entityType),
              isDisplayFunc: (data) => data.entityType,
              isInternationalization: true,
            },
            {
              titleBar: { title: 'message.contact.accountCurrency' },
              key: 'payoutCurrency',
              isDisplayFunc: (data) => data.payoutCurrency,
            },
            {
              titleBar: { title: 'message.contact.bankCountryRegion' },
              key: 'bankCountry',
              textFunc: (data) => (getEnantiomericValue(this.countryList, data.bankCountry) || data.bankCountry),
              isDisplayFunc: (data) => data.bankCountry,
            },
            {
              titleBar: { title: 'message.contact.paymentMethod' },
              key: 'paymentMethod',
              textFunc: (data) => getEnantiomericValue(PAYMENT_METHOD_OPTIONS, data.paymentMethod) || '--',
              isDisplayFunc: (data) => data.paymentMethod,
              isInternationalization: true,
            },
            {
              titleBar: { title: 'message.contact.beneficiaryName' },
              key: 'payee',
              isDisplayFunc: (data) => data.payee,
            },
            {
              titleBar: { title: 'message.contact.certificateNo' },
              key: 'idNumber',
              isDisplayFunc: (data) => data.idNumber,
            },
            { // 手机号
              titleBar: { title: 'message.contact.mobilePhone' },
              key: 'phone',
              isDisplayFunc: (data) => data.phone,
              textFunc: (data) => {
                if (!data.phone) {
                  return '';
                }
                return `${data.areaCode || ''} ${data.phone}`;
              },
            },
            {
              titleBar: { title: 'message.contact.beneficiaryCountry' },
              key: 'payeeCountry',
              textFunc: (data) => getEnantiomericValue(this.countryList, data.payeeCountry),
              isDisplayFunc: (data) => data.payeeCountry,
            },
            {
              titleBar: { title: 'message.contact.beneficiaryAddress' },
              key: 'payeeAddress',
              isDisplayFunc: (data) => data.payeeAddress,
            },
            {
              titleBar: { title: 'message.contact.accountName' },
              key: 'bankAccount',
              isDisplayFunc: (data) => !isPayout && data.bankAccount,
            },
            {
              titleBar: { title: 'message.contact.bankName' },
              key: 'bankName',
              isDisplayFunc: (data) => data.bankName,
            },
            {
              titleBar: { title: 'message.tableCommon.bankAddress' },
              key: 'bankAddress',
              isDisplayFunc: (data) => data.bankAddress,
            },
            {
              titleBar: { title: 'message.contact.accountNumber' },
              key: 'cardNumber',
              isDisplayFunc: (data) => data.cardNumber,
            },
            {
              titleBar: { title: 'message.contact.swiftCode' },
              key: 'swiftCode',
              isDisplayFunc: (data) => data.swiftCode,
            },
            {
              titleBar: { title: isPayout ? 'message.contact.bankCode' : 'message.contact.sortCode' },
              key: 'sortCode',
              isDisplayFunc: (data) => data.sortCode,
            },
            {
              titleBar: { title: 'message.contact.branchCode' },
              key: 'branchCode',
              isDisplayFunc: (data) => data.branchCode,
            },
            {
              titleBar: { title: 'message.contact.file' },
              key: 'material',
              type: 'click',
              buttonType: 'download',
              textFunc: (data) => data.materialName || this.$t('message.contact.downloadMaterial'),
              isDisplayFunc: (data) => (data.material && (!data.materialList || data.materialList.length === 0)),
              func: (data) => {
                window.location.href = data.material;
              },
            },
            {
              titleBar: { title: 'message.tableCommon.remark' },
              key: 'remark',
              isDisplayFunc: (data) => data.remark,
            },
            {
              titleBar: { title: 'message.contact.contactId' },
              key: 'id',
            },
          ];
          const userLogin = this.app.userLogin;
          if (userLogin && (userLogin.businessType === '0')) { // Payout 商户新增显示字段
            const entityType = `${item.entityType}`;
            if (entityType === '0') {
              listTemplate.push({
                titleBar: { title: 'message.contact.companyName' },
                key: 'companyName',
                isDisplayFunc: (data) => data.companyName,
              });
            } else if (entityType === '1') {
              listTemplate.push({
                titleBar: { title: 'message.contact.firstName' },
                key: 'firstName',
                isDisplayFunc: (data) => data.firstName,
              });
              listTemplate.push({
                titleBar: { title: 'message.contact.lastName' },
                key: 'lastName',
                isDisplayFunc: (data) => data.lastName,
              });
            }
            // address: 当“付款地区”为Indonesia、Thailand、United States、Canada、Egypt、Japan、South Korea、Saudi Arabia时必填
            if (['ID', 'TH', 'CA', 'EG', 'SA', 'US', 'JP', 'KR'].includes(item.bankCountry)) {
              listTemplate.push({
                titleBar: { title: 'message.contact.address' },
                key: 'address',
                isDisplayFunc: (data) => data.address1,
                textFunc: (data) => `${data.address1} ${data.address2 || ''} ${data.address3 || ''}`,
              });
            }
            // zipCode: 当“收款行国家/地区”为Canada 或 Egypt时显示
            if (['CA', 'EG'].includes(item.bankCountry)) {
              listTemplate.push({
                titleBar: { title: 'message.contact.zipCode' },
                key: 'zipCode',
                isDisplayFunc: (data) => data.zipCode,
              });
            }
            // vatNumber: 当“收款行国家/地区”为Brazil时必填
            if (['BR'].includes(item.bankCountry)) {
              let vatName = 'CPF';
              if (`${entityType}` === '0') {
                vatName = 'CNPJ';
              }
              listTemplate.push({
                titleBar: { title: vatName },
                key: 'vatNumber',
                isDisplayFunc: (data) => data.vatNumber,
              });
            }
          }
          item.condition = [{
            type: 'default',
            initRows: 10,
            listTemplate,
            data: item,
          }];
        });
        this.listInfo = res;
        this.isLoading = false;
      }).catch(() => {
        this.isLoading = false;
      });
    },
    onCurrentPageChange(index) {
      this.listInfo.current = index;
      this.fetchContactList({ ...this.$route.query, current: index });
    },
    onPageSizeChange(size) {
      this.listInfo.size = size;
      this.listInfo.current = 1;
      this.fetchContactList({ ...this.$route.query, size });
    },
    initQuery() {
      const query = this.$route.query;
      if (query.current) {
        this.listInfo.current = Number(query.current);
      }
      if (query.size) {
        this.listInfo.size = Number(query.size);
      }
    },
    getAuditStatusText(payeeStatus) {
      return getEnantiomericValue(this.auditStatusList, payeeStatus);
    },
    getAuditStatusTagType(payeeStatus) {
      let type = 'info';
      if (payeeStatus === 'pass') {
        type = 'success';
      } else if (payeeStatus === 'fail') {
        type = 'danger';
      } else if (payeeStatus === 'pending_update') {
        type = 'warning';
      }
      return type;
    },
    getCountryText(code) {
      return (getEnantiomericValue(this.countryList, code) || code);
    },
    handleContactDetail(contactId) {
      this.$router.push({ path: '/transfer/contact/edit', query: { contactId } });
    },
    handleTransfer(contactId) {
      this.$router.push({ path: '/transfer/transfer/add/amount', query: { id: contactId } });
    },
    handleSettingDefault(contactId) {
      // 确认框
      this.$refs.customMessage.openHttpModal({
        titleHtml: this.$t('message.common.prompt'),
        contentHtml: `${this.$t('message.payee.makeItDefault')}?`,
        httpFunction: postDefaultCard,
        params: {
          cardInfoId: contactId,
        },
        successFunction: this.fetchContactList,
      }, 'warn');
    },
    handleChangeCollapse(value) {
      if (!value) {
        return;
      }
      this.fetchContactInfo(value);
    },
    async fetchContactInfo(id) {
      let info = this.contactInfoMap[id];
      const materialCondition = [];
      if (!info) {
        info = await getPayeeInfo({ cardInfoId: id });
        if (info.materialList && info.materialList.length > 0) {
          info.materialList.forEach(m => {
            info[`${m.materialName}`] = m.fileUrl;
            let buttonType = '';
            let type = '';
            let text = m.fileUrl;
            if (m.fileUrl && m.fileUrl.startsWith('http')) {
              buttonType = 'download';
              type = 'click';
              text = text = getFileNameFromUrl(m.fileUrl);
            }
            materialCondition.push({
              titleBar: { title: getEnantiomericValue(this.materialNameList, m.materialName) },
              key: m.materialName,
              textFunc: () => text,
              type,
              buttonType,
              isDisplayFunc: () => m.fileUrl,
              func: () => {
                window.location.href = m.fileUrl;
              },
            });
          });
        }
        this.contactInfoMap[id] = info;
      }
      for (let i = 0; i < this.listInfo.content.length; i++) {
        const item = this.listInfo.content[i];
        if (id === item.cardInfoId) {
          item.condition[0].listTemplate = [
            ...item.condition[0].listTemplate,
            ...materialCondition,
          ];
          item.condition[0].data = info;
          return;
        }
      }
    },
    getBankAccountText(item) {
      let bankAccount = item.bankAccount;
      if (!bankAccount) {
        if (item.entityType === '1') {
          bankAccount = item.lastName ? (item.firstName + ' ' + item.lastName) : item.firstName;
        } else {
          bankAccount = item.companyName;
        }
      }
      return bankAccount || '';
    },
    getBankAccount(item) {
      let bankAccount = item.bankAccount;
      if (!bankAccount) {
        if (item.entityType === '1') {
          bankAccount = item.lastName;
        } else {
          bankAccount = item.companyName;
        }
      }
      if (!bankAccount) {
        return '';
      }
      const char = bankAccount.substring(0, 1);
      if (/^[\u4e00-\u9fa5]$/.test(char)) { // 如果是中文
        return char;
      }
      return bankAccount.substring(0, 2).toLocaleUpperCase();
    },
  },
};
</script>
<style lang="scss">
@import '@/styles/variables.scss';
@import '@/styles/pagination.scss';
</style>
<style lang="scss" scoped>
.list-wrapper {
  ::v-deep .el-collapse {
    border-bottom: none;
    border-top: none;
  }
  ::v-deep .el-collapse-item__header {
    height: 80px;
    border-bottom: none;
    padding-left: 11px;
    padding-right: 20px;
  }

  ::v-deep .el-collapse-item.is-active {
    background: #FFFFFF;
    box-shadow: 0px 0px 10px 5px rgba(0,0,0,0.05);
    margin-bottom: 25px;
  }

  ::v-deep .el-collapse-item__content {
    margin-left: 76px;
    margin-right: 20px;
  }

  ::v-deep .el-card__body {
    padding: 0;
  }

  ::v-deep #details .bd-details-wrap:last-child {
    border-bottom: none;
  }

  ::v-deep .el-collapse-item__arrow.el-icon-arrow-right:before {
    font-family: "iconfont" !important;
    content: "\e672";
    color: #0098C7;
  }
}
.contact-item-box {
  display: flex;
  .icon-contact-box {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    background: #F1F5F7;
    border-radius: 50%;
    margin-right: 15px;
    font-size: 14px;
    font-family: Avenir-Heavy, Avenir;
    font-weight: 800;
    color: #264373;
    line-height: 30px;

    .icon-currency {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 17px;
      height: 17px;
      border-radius: 50%;
    }
  }
}

.contact-item-info {
  display: flex;
  flex-direction: column;
  font-size: 14px;
  font-family: Avenir-Book, Avenir;
  font-weight: normal;
  color: rgba(38,67,115,0.4);
  line-height: 32px;

  .contact-name {
    display: inline-block;
    margin-right: 20px;
    font-size: 16px;
    font-family: Avenir-Heavy, Avenir;
    font-weight: 800;
    color: #264373;
    line-height: 22px;
    min-width: 90px;
  }

  .default-contact {
    font-size: 14px;
    font-family: Avenir-Heavy, Avenir;
    font-weight: 600;
    color: #0098C7;
    line-height: 36px;
    margin-left: 40px;
  }

  .contact-country {
    display: inline-block;
    margin-right: 20px;
    min-width: 90px;
  }
}

.item-button-box {
  margin-top: 10px;
  .item-button {
    margin-right: 23px;
    height: 40px;
    font-size: 14px;
    line-height: 36px;
    margin-left: 0;
  }
}

.contact-empty-box {
  margin-top: 80px;
}
</style>
